import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleAlert, HardDrive, Plug, Server } from 'lucide-react';
import { Banner, BrandIcon, Button, Card, EmptyState, PageHeader, StatusPill, TableSkeleton } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { ConnectDrawer } from '@/components/nodes/ConnectDrawer';
import { useRoles } from '@/hooks/useAuth';
import { useNodes } from '@/hooks/useNodes';
import type { NodeView } from '@/types/api';
import { formatGb, formatPercent, formatRelative } from '@/utils/format';
import { NODE_STATE } from '@/utils/labels';
import { diskTone, nodeState } from '@/utils/nodes';
import { can } from '@/utils/permissions';

/** A server's kind, by the mark of what serves its sites: Docker on the panel's own, the web server on an external one. */
function ServerKind({ node }: { readonly node: NodeView }): ReactNode {
  if (node.role !== 'host') {
    return (
      <span className="platform-name text-sm muted">
        <BrandIcon name="docker" size={13} />
        сервер панели
      </span>
    );
  }
  const servers = node.discovery?.webServers ?? (node.discovery?.nginx ? ['nginx'] : []);
  return (
    <span className="platform-name text-sm muted">
      {servers.map((server) => (
        <BrandIcon key={server} name={server === 'apache' ? 'apache' : server === 'docker' ? 'docker' : 'nginx'} size={13} />
      ))}
      внешний сервер
    </span>
  );
}

/**
 * The panel's servers and the external ones it watches. A row opens the
 * server's own page (ADR-0058): its sites, copies and credentials outgrew a
 * drawer.
 */
export function NodesPage(): ReactNode {
  const roles = useRoles();
  const navigate = useNavigate();
  const [connecting, setConnecting] = useState(false);
  const nodes = useNodes({ watch: true });

  const items = nodes.data ?? [];
  const offline = items.filter((node) => node.stale && node.agentVersion !== null);
  const mayEnroll = can(roles, 'nodes.enroll');

  const connect = (
    <Button variant="primary" icon={Plug} onClick={() => setConnecting(true)}>
      Подключить сервер
    </Button>
  );

  return (
    <div className="page">
      <PageHeader
        title="Серверы"
        subtitle="Серверы сайтов панели и внешние серверы"
        actions={mayEnroll ? connect : null}
      />

      {offline.length > 0 ? (
        <Banner tone="warning" icon={CircleAlert}>
          {offline.length === 1
            ? `Сервер ${offline[0]!.name} не отвечает. Состояние его сайтов неизвестно.`
            : `Серверов без ответа: ${offline.length}. Состояние их сайтов неизвестно.`}
        </Banner>
      ) : null}

      <Card flush>
        {nodes.isPending ? (
          <TableSkeleton rows={4} columns={6} />
        ) : nodes.isError ? (
          <ErrorState error={nodes.error} />
        ) : items.length === 0 ? (
          <EmptyState icon={Server} message="Серверов пока нет" {...(mayEnroll ? { action: connect } : {})} />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Сервер</th>
                  <th scope="col">Состояние</th>
                  <th scope="col" className="numeric">
                    Сайтов
                  </th>
                  <th scope="col">Диск</th>
                  <th scope="col">Память</th>
                  <th scope="col">Отклик</th>
                  <th scope="col">Агент</th>
                </tr>
              </thead>
              <tbody>
                {items.map((node) => (
                  <tr
                    key={node.id}
                    className="row-link"
                    tabIndex={0}
                    onClick={() => navigate(`/servers/${node.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') navigate(`/servers/${node.id}`);
                    }}
                  >
                    <td>
                      <div className="stack stack-tight">
                        <span className="strong nowrap">{node.name}</span>
                        <span className="text-sm muted mono">{node.ipv4 ?? node.hostname ?? ''}</span>
                        <ServerKind node={node} />
                      </div>
                    </td>
                    <td>
                      <StatusPill map={NODE_STATE} status={nodeState(node)} />
                    </td>
                    <td className="numeric" title={node.role === 'host' ? 'Сайтов, найденных на сервере' : undefined}>
                      {node.role === 'host' ? (node.discovery?.sites.length ?? '—') : node.siteCount}
                    </td>
                    <td className="nowrap">
                      <span className={`row-tight ${diskTone(node.usedDiskGb, node.totalDiskGb)}`}>
                        <HardDrive size={13} aria-hidden="true" />
                        {formatGb(node.usedDiskGb)} из {formatGb(node.totalDiskGb)}
                        <span>({formatPercent(node.usedDiskGb, node.totalDiskGb)})</span>
                      </span>
                    </td>
                    <td className="muted nowrap">
                      {Math.round(node.usedMemoryMb / 1024)} / {Math.round(node.totalMemoryMb / 1024)} ГБ
                    </td>
                    <td className="muted nowrap" title={node.lastHeartbeatAt ?? ''}>
                      {formatRelative(node.lastHeartbeatAt)}
                    </td>
                    <td className="mono muted">{node.agentVersion ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConnectDrawer open={connecting} onClose={() => setConnecting(false)} />
    </div>
  );
}
